import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import {
	DailyBonusCoinsPng,
	DailyBonusStarSvg,
	DayArrowSvg,
	DayCollectedSvg,
	VipCoinsPng,
} from '../../../assets/bonuses';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import CtaButton from '../common/CtaButton';
import { groupThousands } from '../Event/eventTheme';
import {
	BONUS_FONT,
	BONUS_FONT_CONDENSED,
	bonusColors,
	bonusGlow,
	CARD_FILL,
	CHIP_DIM_FILL,
	RADIUS_PILL,
	RADIUS_SECTION,
} from './bonusesTheme';

// Figma "Daily Bonus / 5 days" (3491:6192) and its day states (3491:6149 Current, 6150 Collected,
// 6151 Locked). The file's own note on the states: "Current = gold ring, Collected = warm chip
// with green check, Locked = dim chip, dim day label, 50% coins."
export const DAILY_BONUS_DESIGN_WIDTH = 370;
export const DAILY_BONUS_DESIGN_HEIGHT = 103;
export const DAILY_BONUS_VIP_DESIGN_HEIGHT = 123;

// The star's visible center is slightly below its SVG box center, so it sits 3px above the title's
// line-box center to align with the text glyphs.
const HEADING = { y: 7, starX: 13, starY: 9, starW: 11, starH: 10, titleX: 28, fontSize: 16 };
// "Rewards and collect": a flex row at 7/31, 354 wide, gap 4 - five 45-wide days with 7-wide
// arrows between them, then the 65-wide collect column.
const ROW = { x: 7, y: 31, gap: 4 };
const DAY = { width: 45, height: 60, vipHeight: 88 };
const ARROW = { width: 7, height: 28, chevron: 13 };
const DAY_PITCH = DAY.width + ROW.gap + ARROW.width + ROW.gap;
const COLLECT = { width: 65, height: 56, labelFontSize: 9, buttonHeight: 28 };
const COLLECT_X = ROW.x + 4 * DAY_PITCH + DAY.width + ROW.gap;
const COLLECT_Y = ROW.y + (DAY.height - COLLECT.height) * 0.5;
// Inside a day: the label, a 4 gap, then the 37x44 chip pill with 9 of padding over the coins.
const DAY_LABEL = { fontSize: 10, height: 12 };
const DAY_CHIP = { width: 37, height: 44, y: 16, paddingTop: 9 };
const DAY_COINS = { width: 27, height: 15 };
const VIP_COINS = { width: 12, height: 8, x: 2, y: 63 };
const DAY_AMOUNT = { fontSize: 10 };
/** The tick hangs off the foot of the tile, centred. */
const DAY_BADGE = { size: 14, y: 52 };

export type DailyBonusDay = {
	day: number;
	chips: number;
	vipChips: number;
};

type DayState = 'current' | 'collected' | 'locked';

type Props = {
	days: DailyBonusDay[];
	/** The day that can be collected now; everything before it is already banked. */
	currentDay?: number;
	/** False after a collect until the cooldown runs out; the button greys out and ignores presses. */
	canCollect?: boolean;
	isVip?: boolean;
	onCollect?: () => void;
	width: number;
};

const LABEL_COLOR: Record<DayState, string> = {
	current: bonusColors.accent,
	collected: bonusColors.gold50,
	locked: bonusColors.locked,
};

const AMOUNT_COLOR: Record<DayState, string> = {
	current: bonusColors.accent,
	collected: bonusColors.gold100,
	locked: bonusColors.locked,
};

export const DailyBonusStrip = ({
	days,
	currentDay,
	canCollect = false,
	isVip = false,
	onCollect,
	width,
}: Props) => {
	const { t } = useTranslation();
	const scale = width / DAILY_BONUS_DESIGN_WIDTH;
	const styles = useMemo(() => createStyles(scale, isVip), [isVip, scale]);
	const currentFillId = useSvgId('dailyBonusChipCurrent');
	const dimFillId = useSvgId('dailyBonusChipDim');

	const chipPlate = (state: DayState) => (
		<View
			pointerEvents='none'
			style={[styles.dayChip, state === 'current' ? styles.dayChipCurrent : styles.dayChipDim]}
		>
			<Svg width='100%' height='100%'>
				<Defs>
					<LinearGradient id={currentFillId} x1='0' y1='0' x2='0' y2='1'>
						{CARD_FILL.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
					<LinearGradient id={dimFillId} x1='0' y1='0' x2='0' y2='1'>
						{CHIP_DIM_FILL.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				</Defs>
				<Rect
					x='0'
					y='0'
					width='100%'
					height='100%'
					fill={`url(#${state === 'current' ? currentFillId : dimFillId})`}
				/>
			</Svg>
		</View>
	);

	return (
		<View style={styles.root}>
			<View style={styles.star}>
				<DailyBonusStarSvg width={HEADING.starW * scale} height={HEADING.starH * scale} />
			</View>
			<Text numberOfLines={1} style={styles.heading}>
				{t('T_SITE_BONUSES_HEADING', { defaultValue: 'Daily Bonus' }).toUpperCase()}
			</Text>

			{days.map((entry, index) => {
				const state: DayState =
					currentDay != null && entry.day === currentDay
						? 'current'
						: currentDay != null && entry.day < currentDay
							? 'collected'
							: 'locked';

				return (
					<React.Fragment key={entry.day}>
						<View style={[styles.day, { left: (ROW.x + index * DAY_PITCH) * scale }]}>
							<Text numberOfLines={1} style={[styles.dayLabel, { color: LABEL_COLOR[state] }]}>
								{t('T_SITE_MODAL_DAILY_BONUS_DAY', { defaultValue: 'day' }).toUpperCase()}{' '}
								{entry.day}
							</Text>

							{chipPlate(state)}

							<Image
								source={DailyBonusCoinsPng}
								style={[styles.dayCoins, state === 'locked' && styles.dayCoinsLocked]}
								resizeMode='cover'
							/>

							<Text numberOfLines={1} style={[styles.dayAmount, { color: AMOUNT_COLOR[state] }]}>
								{groupThousands(entry.chips)}
							</Text>

							{isVip ? (
								<>
									<Image
										source={VipCoinsPng}
										style={[styles.vipCoins, state === 'locked' && styles.dayCoinsLocked]}
										resizeMode='contain'
									/>
									<Text
										numberOfLines={1}
										style={[styles.vipAmount, { color: AMOUNT_COLOR[state] }]}
									>
										+{groupThousands(entry.vipChips)}
									</Text>
								</>
							) : null}

							{state === 'collected' ? (
								<View style={styles.dayBadge}>
									<DayCollectedSvg width={DAY_BADGE.size * scale} height={DAY_BADGE.size * scale} />
								</View>
							) : null}
						</View>

						{index < days.length - 1 ? (
							<View
								style={[
									styles.arrow,
									{ left: (ROW.x + DAY.width + ROW.gap + index * DAY_PITCH) * scale },
								]}
							>
								<DayArrowSvg width={ARROW.width * scale} height={ARROW.chevron * scale} />
							</View>
						) : null}
					</React.Fragment>
				);
			})}

			{/* Same Android widening as the card titles: the 65-wide column clipped this to
			    "COLLECT YOUR..." on device. */}
			<Text adjustsFontSizeToFit numberOfLines={2} style={styles.collectLabel}>
				{t('T_SITE_DAILY_BONUS_COLLECT_PROMPT', { defaultValue: 'COLLECT YOUR\nDAILY BONUS!' })}
			</Text>
			<CtaButton
				label={t('T_SITE_LOBBY_COLLECT', { defaultValue: 'COLLECT' })}
				onPress={onCollect}
				disabled={!canCollect}
				showNotificationDot={canCollect}
				labelStyle={{
					fontSize: 12 * scale,
					lineHeight: 12 * scale,
					textAlign: 'center',
					top: scale,
				}}
				style={[
					styles.collectButton,
					{ width: COLLECT.width * scale, height: COLLECT.buttonHeight * scale },
				]}
				surfaceStyle={{ minHeight: COLLECT.buttonHeight * scale, paddingHorizontal: 2 }}
			/>
		</View>
	);
};

const createStyles = (scale: number, isVip: boolean) =>
	StyleSheet.create({
		// The strip is filled flat, not with a ramp: the file layers an opaque surface over the
		// gradient the cards use, so only the surface shows.
		root: {
			width: DAILY_BONUS_DESIGN_WIDTH * scale,
			height: (isVip ? DAILY_BONUS_VIP_DESIGN_HEIGHT : DAILY_BONUS_DESIGN_HEIGHT) * scale,
			borderRadius: RADIUS_SECTION * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: bonusColors.sectionBorder,
			backgroundColor: bonusColors.stripSurface,
			overflow: 'hidden',
			...bonusGlow.section,
		},
		star: {
			position: 'absolute',
			left: HEADING.starX * scale,
			top: HEADING.starY * scale,
		},
		// Sofia Sans Condensed SemiBold 16, line-height 1.1, gold/150, tracking 0.64, uppercase.
		heading: {
			position: 'absolute',
			left: HEADING.titleX * scale,
			top: HEADING.y * scale,
			color: bonusColors.gold150,
			fontFamily: BONUS_FONT_CONDENSED,
			fontSize: HEADING.fontSize * scale,
			fontWeight: '600',
			letterSpacing: 0.64 * scale,
			lineHeight: HEADING.fontSize * 1.1 * scale,
		},
		day: {
			position: 'absolute',
			top: ROW.y * scale,
			width: DAY.width * scale,
			height: (isVip ? DAY.vipHeight : DAY.height) * scale,
			alignItems: 'center',
		},
		// Figma "DS/Label/S": Sofia Sans SemiBold 10, line-height 1.2.
		dayLabel: {
			position: 'absolute',
			top: 0,
			fontFamily: BONUS_FONT,
			fontSize: DAY_LABEL.fontSize * scale,
			fontWeight: '600',
			lineHeight: DAY_LABEL.height * scale,
		},
		dayChip: {
			position: 'absolute',
			left: (DAY.width - DAY_CHIP.width) * 0.5 * scale,
			top: DAY_CHIP.y * scale,
			width: DAY_CHIP.width * scale,
			height: DAY_CHIP.height * scale,
			borderRadius: RADIUS_PILL * scale,
			borderWidth: Math.max(1, 1 * scale),
			overflow: 'hidden',
		},
		dayChipCurrent: {
			borderColor: bonusColors.gold300,
			...bonusGlow.chip,
		},
		dayChipDim: {
			borderColor: bonusColors.chipDimBorder,
		},
		dayCoins: {
			position: 'absolute',
			left: (DAY.width - DAY_COINS.width) * 0.5 * scale,
			top: (DAY_CHIP.y + DAY_CHIP.paddingTop) * scale,
			width: DAY_COINS.width * scale,
			height: DAY_COINS.height * scale,
		},
		dayCoinsLocked: {
			opacity: 0.5,
		},
		vipCoins: {
			position: 'absolute',
			left: VIP_COINS.x * scale,
			top: VIP_COINS.y * scale,
			width: VIP_COINS.width * scale,
			height: VIP_COINS.height * scale,
		},
		dayAmount: {
			position: 'absolute',
			top: (DAY_CHIP.y + DAY_CHIP.paddingTop + DAY_COINS.height) * scale,
			fontFamily: BONUS_FONT,
			fontSize: DAY_AMOUNT.fontSize * scale,
			fontWeight: '600',
			lineHeight: DAY_AMOUNT.fontSize * 1.2 * scale,
		},
		vipAmount: {
			position: 'absolute',
			top: VIP_COINS.y * scale,
			left: (VIP_COINS.x + VIP_COINS.width + 1) * scale,
			fontFamily: BONUS_FONT,
			fontSize: 8 * scale,
			fontWeight: '700',
			lineHeight: 8 * scale,
		},
		dayBadge: {
			position: 'absolute',
			left: (DAY.width - DAY_BADGE.size) * 0.5 * scale,
			top: (isVip ? VIP_COINS.y + 11 : DAY_BADGE.y) * scale,
		},
		// The file notes the 7x28 arrow box is intentional: the row centres its children and that
		// height lands the chevron on the chips rather than on the day labels.
		arrow: {
			position: 'absolute',
			top: (ROW.y + (DAY.height - ARROW.height) * 0.5 + 8) * scale,
		},
		// Sofia Sans Condensed Black 9, line-height 10, text/accent, tracking 0.36, uppercase.
		collectLabel: {
			position: 'absolute',
			left: COLLECT_X * scale,
			top: COLLECT_Y * scale,
			width: COLLECT.width * scale,
			color: bonusColors.accent,
			fontFamily: BONUS_FONT_CONDENSED,
			fontSize: COLLECT.labelFontSize * scale,
			fontWeight: '900',
			letterSpacing: 0.36 * scale,
			lineHeight: 10 * scale,
			textAlign: 'center',
		},
		collectButton: {
			position: 'absolute',
			left: COLLECT_X * scale,
			top: (COLLECT_Y + COLLECT.height - COLLECT.buttonHeight) * scale,
		},
	});

export default DailyBonusStrip;
